/* eslint-disable react/jsx-props-no-spreading, jsdoc/require-returns-check, jsdoc/check-param-names -- forwardRef destructured props */
import { forwardRef } from 'react';

// Utils
import cn from '../../../utils/cn';

// Styles
import style from './Radio.module.css';

/**
 * @typedef {'small'|'large'} RadioSize
 */

const Radio = forwardRef(
  /**
   * @param {object} props - Component props.
   * @param {string} props.id - Required unique identifier for the radio button
   * @param {string} props.name - Name of the radio button
   * @param {string} props.value - Value of the radio button
   * @param {() => void} props.onChange - Change handler
   * @param {string} [props.className] - Additional CSS classes
   * @param {string | React.ReactNode} [props.label] - Text or composite label for the radio button
   * @param {boolean} [props.checked = false] - Whether radio button is checked (controlled)
   * @param {RadioSize} [props.size = 'large'] - Radio button size
   * @param {boolean} [props.disabled = false] - Whether radio button is disabled
   * @param {boolean} [props.isReadOnly = false] - Locked; value remains in form
   * @param {boolean} [props.multiline = false] - Allow label to wrap and grow row height (e.g. composite labels)
   * @param {'primary' | 'black'} [props.theme = 'primary'] - Theme of the radio button
   * @param {React.Ref<HTMLInputElement>} ref - Forwarded ref to the input element
   * @returns {JSX.Element} Radio component
   */
  ({
    label,
    size = 'large',
    className = '',
    name,
    onChange,
    theme = 'primary',
    disabled = false,
    isReadOnly = false,
    multiline = false,
    ...props
  }, ref) => (
    <label
      htmlFor={props.id}
      className={cn([
        style['radio-container'],
        disabled && style['is-disabled'],
        isReadOnly && style['is-read-only'],
        multiline && style.multiline,
        style[theme],
        style[size],
        className,
      ])}
    >
      <input
        ref={ref}
        type="radio"
        className={style['radio-input']}
        disabled={disabled}
        {...props}
        name={name}
        onChange={isReadOnly ? () => {} : onChange}
      />
      <span className={cn([style['radio-checkmark']])} />
      {label ? <span className={style['radio-label']}>{label}</span> : null}
    </label>
  ),
);

export default Radio;
